import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import {
  BaggageSelectors,
  type CartUpdateEvents,
  CartUpdateEventType,
  commonSelectors,
  GenericChildState,
  getChildState,
  useCheckoutSend,
  useCheckoutStateSelector as useSelector,
} from "@hopper-b2b/checkout";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Spinner } from "@hopper-b2b/ui";
import { ErrorScreen } from "../../../components/ErrorScreen";
import { PATH_FLIGHTS_SEARCH } from "../../../utils/urlPaths";
import { Event, type TEvent } from "../events";
import styles from "./CartUpdate.module.scss";

export const CartUpdateComponent = () => {
  const [retryCount, setRetryCount] = useState(0);
  const send = useCheckoutSend<CartUpdateEvents | TEvent>();
  const { t } = useI18nContext();

  const nagivate = useNavigate();
  const stateValue = useSelector(commonSelectors.getStateValue) as string;
  const childState = getChildState(stateValue);
  const maxRetryCount = 3;
  const bagsInCartUpdate = useSelector(BaggageSelectors.areBagsInCartUpdate);

  const removeBagsAndTryAgain = useCallback(() => {
    send(Event.DELETE_BAGS_FROM_CART_UPDATE);
  }, [send]);

  const retryCartUpdate = useCallback(() => {
    setRetryCount(retryCount + 1);
    send(CartUpdateEventType.RETRY_CART_UPDATE);
  }, [retryCount, send]);

  const goBackToSearch = useCallback(() => {
    send(CartUpdateEventType.CLEAR_CART_UPDATE_ERROR);
    nagivate("/" + PATH_FLIGHTS_SEARCH);
  }, [nagivate, send]);

  const errorScreenProps = useMemo(
    () =>
      bagsInCartUpdate
        ? {
            title: t("checkoutError.genericErrorTitle"),
            subtitle: t("checkoutError.baggageErrorSubtitle"),
            primaryOnClick: removeBagsAndTryAgain,
            primaryButtonText: t("baggage.continueWithoutBags"),
          }
        : {
            title: t("checkoutError.genericErrorTitle"),
            subtitle: t("checkoutError.genericErrorSubtitle"),
            primaryOnClick: goBackToSearch,
            primaryButtonText: t("checkoutError.findNewFlight"),
          },
    [bagsInCartUpdate, goBackToSearch, removeBagsAndTryAgain, t]
  );

  return childState === GenericChildState.error ? (
    <ErrorScreen
      {...errorScreenProps}
      {...(retryCount !== maxRetryCount
        ? {
            secondaryOnClick: retryCartUpdate,
            secondaryButtonText: t("checkoutError.tryAgain"),
          }
        : {})}
      className="warn"
    />
  ) : (
    <div className={styles.CartUpdateLoader}>
      <Spinner />
    </div>
  );
};
